
import React, { useState } from 'react';
import { 
  ArrowLeft, 
  Wallet, 
  Search, 
  Zap, 
  ShieldAlert, 
  CheckCircle2, 
  ExternalLink, 
  Activity, 
  History, 
  ArrowRight,
  TrendingUp,
  Globe,
  ArrowUpRight,
  ArrowDownLeft,
  Filter,
  Download,
  Copy,
  ChevronRight,
  Plus,
  FileText,
  Loader2,
  // Added missing Database icon
  Database
} from 'lucide-react';

interface Transaction {
  hash: string;
  type: 'Incoming' | 'Outgoing';
  amount: string;
  asset: string;
  counterparty: string;
  date: string;
  risk: 'Clean' | 'Suspicious' | 'High-Risk';
}

export const BlockchainView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [address, setAddress] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [results, setResults] = useState<{
    address: string;
    riskScore: number;
    label: string;
    lastTx: string;
    totalVolume: string;
    nexus: string;
    indicators: string[];
    transactions: Transaction[];
  } | null>(null);

  const handleSearch = () => {
    if (!address) return;
    setIsSearching(true);
    setTimeout(() => {
      setResults({
        address,
        riskScore: 24,
        label: 'Clean',
        lastTx: '14m ago',
        totalVolume: '142.5 ETH',
        nexus: 'Direct deposit from Binance',
        indicators: ['Exchange Known', 'KYC Verified Hotwallet', 'Non-Mixer Activity', 'Whale Profile'],
        transactions: [
          { hash: '0x84f1b2c...a210', type: 'Incoming', amount: '12.4000', asset: 'ETH', counterparty: 'Binance: Hotwallet 4', date: '2023-10-28 14:22:01', risk: 'Clean' },
          { hash: '0x321a99d...b442', type: 'Outgoing', amount: '2.5000', asset: 'ETH', counterparty: 'Uniswap V3: Router', date: '2023-10-28 11:05:44', risk: 'Clean' },
          { hash: '0x1192e3a...c881', type: 'Incoming', amount: '0.8420', asset: 'ETH', counterparty: 'Unknown Wallet (0x12..)', date: '2023-10-27 18:44:12', risk: 'Suspicious' },
          { hash: '0xfa04d11...d993', type: 'Outgoing', amount: '1.2000', asset: 'ETH', counterparty: 'Coinbase: Custody', date: '2023-10-27 09:12:30', risk: 'Clean' },
          { hash: '0xbb22c44...e102', type: 'Incoming', amount: '450.00', asset: 'USDC', counterparty: 'Circle: Mint', date: '2023-10-26 22:15:00', risk: 'Clean' },
          { hash: '0x77dd11a...f994', type: 'Outgoing', amount: '0.0500', asset: 'ETH', counterparty: 'Tornado.cash (Indirect)', date: '2023-10-26 14:02:11', risk: 'High-Risk' },
        ]
      });
      setIsSearching(false);
    }, 1500);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="p-6 space-y-8 animate-in slide-in-from-right duration-500 max-w-7xl mx-auto pb-20">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center space-x-4">
          <button onClick={onBack} className="p-3 bg-white border border-slate-200 rounded-2xl hover:bg-slate-100 transition-all text-slate-600 shadow-sm">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">Blockchain Intel</h1>
            <p className="text-sm text-slate-500 font-medium">Crystal-AI integrated forensics & bulk wallet surveillance.</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => setIsBulkOpen(true)}
            className="bg-white border border-slate-200 text-slate-700 px-6 py-2.5 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-slate-50 shadow-sm transition-all flex items-center space-x-2"
          >
             <FileText size={16} />
             <span>Bulk Screening Node</span>
          </button>
        </div>
      </div>

      <div className="bg-slate-900 p-8 md:p-12 rounded-[3.5rem] shadow-2xl relative overflow-hidden group">
         <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-white text-xl font-black flex items-center">
                 <Search size={20} className="mr-3 text-blue-400" /> Wallet Forensic Screener
              </h2>
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">v4.1.0-Forensic</span>
            </div>
            <div className="flex flex-col md:flex-row items-center bg-white/5 border border-white/10 rounded-3xl p-2 focus-within:ring-4 focus-within:ring-blue-500/20 transition-all gap-2">
               <input 
                 type="text" 
                 value={address}
                 onChange={(e) => setAddress(e.target.value)}
                 onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                 placeholder="0x... or Bitcoin Legacy/SegWit Address" 
                 className="flex-1 bg-transparent border-none outline-none px-6 py-4 text-white font-mono text-sm placeholder:text-slate-500 w-full"
               />
               <button 
                 onClick={handleSearch}
                 disabled={isSearching}
                 className="bg-blue-600 text-white px-12 py-4 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-blue-700 transition-all active:scale-95 disabled:opacity-50 w-full md:w-auto flex items-center justify-center shadow-lg shadow-blue-500/20"
               >
                 {isSearching ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div> : <Zap size={16} className="mr-2"/>}
                 {isSearching ? 'Scanning Registry...' : 'Run Analysis'}
               </button>
            </div>
         </div>
         <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600 rounded-full blur-[140px] opacity-10 translate-x-1/2 -translate-y-1/2"></div>
      </div>

      {results && !isSearching && (
        <div className="space-y-8 animate-in zoom-in-95 fade-in duration-500">
           <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4 space-y-6">
                 <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm text-center relative overflow-hidden">
                    <div className="relative z-10">
                      <div className="relative inline-block mb-6">
                         <div className={`w-28 h-28 rounded-[2.5rem] flex items-center justify-center shadow-2xl border-4 border-white transition-all ${
                           results.riskScore > 70 ? 'bg-rose-50 text-rose-500 shadow-rose-100' : 'bg-emerald-50 text-emerald-600 shadow-emerald-100'
                         }`}>
                            {results.riskScore > 70 ? <ShieldAlert size={48} /> : <CheckCircle2 size={48} />}
                         </div>
                      </div>
                      <h3 className="text-3xl font-black text-slate-900 mb-1 leading-none uppercase tracking-tighter">{results.label}</h3>
                      <p className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em] mb-8">Risk Severity: {results.riskScore}%</p>
                      
                      <div className="space-y-4 text-left border-t border-slate-100 pt-6">
                         <DetailRow label="Account Nexus" value={results.nexus} />
                         <DetailRow label="Sanctions Check" value="Clear" valueColor="text-emerald-600" />
                         <DetailRow label="Last Scanned" value={results.lastTx} />
                      </div>
                    </div>
                 </div>

                 <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center">
                       <Filter size={12} className="mr-2" /> Risk Indicators
                    </h4>
                    <div className="flex flex-wrap gap-2">
                       {results.indicators.map((ind, i) => (
                         <span key={i} className="px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-black text-slate-600 uppercase tracking-tight">
                            {ind}
                         </span>
                       ))}
                    </div>
                 </div>
              </div>

              <div className="lg:col-span-8 h-full">
                 <div className="bg-white p-10 rounded-[2.5rem] border border-slate-200 shadow-sm h-full flex flex-col">
                    <div className="flex items-center justify-between mb-10">
                       <div className="flex items-center space-x-3">
                         <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl shadow-sm"><Activity size={20} /></div>
                         <div>
                            <h4 className="text-lg font-black text-slate-900 tracking-tight uppercase">Address Intelligence</h4>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Deep-Chain Association analysis</p>
                         </div>
                       </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 flex-1">
                       <div className="space-y-6">
                          <div>
                             <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">Observed Wallet</label>
                             <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100 font-mono text-xs break-all text-slate-700 shadow-inner group">
                                <span className="flex-1 truncate mr-4">{results.address}</span>
                                <button onClick={() => copyToClipboard(results.address)} className="text-slate-300 hover:text-blue-600 transition-colors">
                                   <Copy size={14} />
                                </button>
                             </div>
                          </div>
                       </div>

                       <div className="grid grid-cols-2 gap-6">
                          <MetricBox label="Volume (All-Time)" value={results.totalVolume} sub="+12.4% MoM" subColor="text-emerald-500" />
                          <MetricBox label="Wallet Age" value="2.8 Years" sub="Maturity: High" subColor="text-blue-500" />
                          <MetricBox label="Global Ranking" value="#12,402" sub="by Asset Value" subColor="text-slate-400" />
                          <MetricBox label="Peer Network" value="842 Nodes" sub="Cluster Density" subColor="text-amber-500" />
                       </div>
                    </div>

                    <div className="mt-10 pt-8 border-t border-slate-100 flex items-center justify-between">
                       <div className="flex items-center space-x-3">
                          <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full shadow-[0_0_10px_rgba(16,185,129,0.5)]"></div>
                          <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Surveillance Pulse Active</span>
                       </div>
                       <button className="px-8 py-3.5 bg-slate-900 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-2xl shadow-slate-200">
                          Export Compliance Report
                       </button>
                    </div>
                 </div>
              </div>
           </div>
        </div>
      )}

      {/* Bulk Screening Slide-over */}
      {isBulkOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden flex justify-end">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300" onClick={() => setIsBulkOpen(false)}></div>
          <div className="relative w-full max-w-xl bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-500 rounded-l-[3.5rem] border-l border-slate-100 overflow-hidden">
             <div className="p-10 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-blue-600 text-white rounded-2xl shadow-lg shadow-blue-200">
                    <Database size={24} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-slate-900 tracking-tight">Bulk Forensic Node</h2>
                    <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mt-1">Parallel Cross-Chain Risk Assessment</p>
                  </div>
                </div>
             </div>

             <div className="flex-1 overflow-y-auto p-10 space-y-10">
                <div className="space-y-6">
                   <h3 className="text-xs font-black text-blue-600 uppercase tracking-widest flex items-center">
                     <Plus size={14} className="mr-2" /> Add Target Addresses
                   </h3>
                   <div className="p-6 bg-slate-50 border-2 border-dashed border-slate-200 rounded-[2.5rem] flex flex-col items-center justify-center text-center space-y-4 hover:border-blue-400 hover:bg-white transition-all cursor-pointer group">
                      <div className="p-4 bg-white rounded-2xl text-slate-400 group-hover:text-blue-600 shadow-sm"><FileText size={32} /></div>
                      <div>
                        <p className="text-sm font-black text-slate-900 leading-none mb-1">Upload CSV or JSON</p>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Single column of addresses preferred</p>
                      </div>
                   </div>
                   <div className="relative">
                      <textarea 
                        className="w-full h-48 px-6 py-6 bg-slate-50 border border-slate-200 rounded-[2.5rem] text-sm font-mono focus:bg-white focus:ring-4 focus:ring-blue-500/10 outline-none transition-all" 
                        placeholder="Paste addresses line by line...&#10;0x123...&#10;0x456...&#10;bc1q7..."
                      ></textarea>
                   </div>
                </div>

                <div className="p-6 bg-blue-50/50 rounded-3xl border border-blue-100 flex items-start space-x-4">
                  <div className="p-2 bg-white rounded-xl shadow-sm text-blue-600"><Zap size={18} /></div>
                  <div>
                     <p className="text-[11px] font-black text-blue-900 uppercase tracking-tight">Concurrent API Quota</p>
                     <p className="text-xs text-blue-700 leading-relaxed mt-1">Bulk tasks are processed at 50 requests/min. Results will appear in the Screening History tab.</p>
                  </div>
                </div>
             </div>

             <div className="p-10 border-t border-slate-100 bg-slate-50 flex space-x-4 rounded-bl-[3.5rem]">
               <button onClick={() => setIsBulkOpen(false)} className="flex-1 py-4 bg-white border border-slate-200 rounded-2xl text-[11px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-100 transition-all active:scale-95 shadow-sm">
                 Cancel
               </button>
               <button className="flex-1 py-4 bg-slate-900 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-slate-800 shadow-xl shadow-slate-200 transition-all active:scale-95">
                 Queue {0} Addresses
               </button>
             </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailRow: React.FC<{ label: string, value: string, valueColor?: string }> = ({ label, value, valueColor = 'text-slate-900' }) => (
  <div className="flex justify-between items-center text-[11px] font-black uppercase tracking-widest">
    <span className="text-slate-400">{label}</span>
    <span className={valueColor}>{value}</span>
  </div>
);

const MetricBox: React.FC<{ label: string, value: string, sub: string, subColor: string }> = ({ label, value, sub, subColor }) => (
  <div className="p-6 bg-slate-50 border border-slate-100 rounded-[2.5rem] flex flex-col justify-between hover:bg-white hover:shadow-md transition-all group">
     <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</p>
     <p className="text-xl font-black text-slate-900 my-2">{value}</p>
     <div className={`flex items-center text-[10px] font-black uppercase ${subColor}`}>
        <TrendingUp size={12} className="mr-1" /> {sub}
     </div>
  </div>
);
